import {LitElement, html, css} from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { loadGlobalStyles } from '../components/slds-system';
import '../components/slds-dropdown.js';
import '../components/slds-lookup.js';
import { connect } from 'pwa-helpers/connect-mixin.js';
import store from '../src/store';
import { resetStateField } from '../src/store';
import { fetchImages, createPost } from '../src/apiActions';
import { addNotification, askToLogin } from '../src/store';
import { SldsBaseElement } from '../components/slds-base-element.js';

@customElement('post-view')
class PostView extends connect(store)(SldsBaseElement) {
  constructor() {
    super();
    this.title = localStorage.getItem('postTitleInput') || '';
    this.selectedCategory = JSON.parse(localStorage.getItem('selectedCategoryId')) || null;
    this.selectedTags = JSON.parse(localStorage.getItem('selectedTags')) || [];
  }

  @property({ type: Boolean })
  urlUpload = false;

  @property({ type: Object })
  dropzone = null;

  @property({ type: Array })
  categories = [];

  @property({ type: Object })
  selectedCategory = null;

  @property({ type: String })
  accessToken = '';

  @property({ type: Object })
  currentFile = null;

  @property({ type: Array })
  currentFiles = null;

  @property({ type: Array })
  selectedTags = [];

  @property({ type: String })
  activeRoute = '';

  @property({ type: Boolean })
  sendingRequest = false;

  @property({ type: Boolean })
  uploadingUrl = false;

  updated(changedProperties) {
    if (changedProperties.has('activeRoute') && this.activeRoute === 'post') {
      store.dispatch(fetchImages());
    }
    if (changedProperties.has('categories')) {
      if (this.selectedCategory && !this.categories.some(category => category.id === this.selectedCategory?.id)) {
        this.selectedCategory = null;
      }
    }
  }

  firstUpdated() {
    super.firstUpdated();
    this.initDropzone();
    if (this.title) {
      this.updateCharCount({ target: { value: this.title, maxLength: 280 } });
    }
    this.tagLookup = this.shadowRoot.querySelector('#tag-lookup');
    this.tagLookup.url = window.backendUrl + '/api/upload/search';
  }

  handleTagSelected(event) {
    this.selectedTags = event.detail.selectedItems;
    localStorage.setItem('selectedTags', JSON.stringify(this.selectedTags));
  }

  chooseFile(event) {
    event.preventDefault();
    if (this.dropzone) {
      this.dropzone.hiddenFileInput.click();
    }
  }

  stateChanged(state) {
    this.accessToken = state.accessToken;
    this.categories = state.categories || [];
    
    if (state.createdPost) {
      this.sendingRequest = false;
      const createdPost = state.createdPost;
      store.dispatch(resetStateField('createdPost'));
      if (createdPost.status === 'success') {
        window.location.href = `/post/${createdPost.postId}`;
      } else {
        store.dispatch(addNotification({
          type: 'error',
          content: {
            heading: 'Post creation failed',
            message: createdPost.error
          }
        }));
      }
    }

    if (state.images && state.images.length > 0) {
      this.currentFiles = state.images;
      store.dispatch(resetStateField('images'));
      this.updatePreview(this.currentFiles[0]);
    }

    if (!state.user && !this.needToLogin && state.route === 'post') {
      this.needToLogin = true;
      store.dispatch(askToLogin(true));
    }
  }

  updatePreview(file) {
    if (!file || !file.file_url) return;

    const previewElement = this.shadowRoot.querySelector('#previews');
    if (!previewElement) return;

    previewElement.innerHTML = '';

    const previewContainer = document.createElement('div');
    previewContainer.className = 'preview-container';
    previewContainer.style.width = '100%';
    previewContainer.style.maxHeight = '500px';
    previewContainer.style.display = 'flex';
    previewContainer.style.justifyContent = 'center';
    previewContainer.style.alignItems = 'center';
    previewContainer.style.overflow = 'hidden';

    // Try to determine mime type from URL if not provided
    const mimeType = file.mime_type || (file.file_url.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? 'image' : 'video');
    const isImage = mimeType.startsWith('image') || mimeType.includes('image');
    const isVideo = mimeType.startsWith('video') || mimeType.includes('video');

    if (isImage) {
      const img = document.createElement('img');
      img.src = file.file_url;
      img.style.maxWidth = '100%';
      img.style.maxHeight = '500px';
      img.style.objectFit = 'contain';
      previewContainer.appendChild(img);
    } else if (isVideo) {
      const video = document.createElement('video');
      video.src = file.file_url;
      video.controls = true;
      video.style.maxWidth = '100%';
      video.style.maxHeight = '500px';
      previewContainer.appendChild(video);
    }

    previewElement.appendChild(previewContainer);
  }

  createDefaultPreview(previewElement) {
    const messageDiv = document.createElement('div');
    messageDiv.className = 'dz-default dz-message';
    messageDiv.innerHTML = `
      <span class="slds-icon_container slds-icon__svg--default">
        <svg class="slds-icon slds-icon_large slds-icon-text-default" aria-hidden="true">
          <use xlink:href="/assets/icons/utility-sprite/svg/symbols.svg#image"></use>
        </svg>
      </span>
      <p>Choose a photo or video to upload</p>
    `;
    previewElement.appendChild(messageDiv);
  }

  async initDropzone() {
    try {
      const { Dropzone } = await import('dropzone');
      const dropzoneCss = await import('dropzone/dist/dropzone.css');
      
      const style = document.createElement('style');
      style.textContent = dropzoneCss.default;
      this.shadowRoot.appendChild(style);

      const dropzoneElement = this.shadowRoot.querySelector('#dropzone');
      const previewElement = this.shadowRoot.querySelector('#previews');

      // Clear any existing content and create default message
      previewElement.innerHTML = '';
      this.createDefaultPreview(previewElement);

      const config = {
        url: window.backendUrl + '/api/upload',
        maxFiles: 1,
        acceptedFiles: "image/*, video/*",
        maxFilesize: 256,
        thumbnailWidth: 120,
        thumbnailHeight: 120,
        thumbnailMethod: 'contain',
        previewsContainer: previewElement,
        clickable: true,
        dictDefaultMessage: "Drop files here or click to upload",
        headers: {
          'Authorization': this.accessToken
        }
      };

      this.dropzone = new Dropzone(dropzoneElement, config);

      this.dropzone.on('sending', (file, xhr, formData) => {
        xhr.setRequestHeader('Authorization', this.accessToken);
      });

      this.dropzone.on('addedfile', (file) => {
        if (this.currentFile) {
          this.dropzone.removeFile(this.currentFile);
        }
        this.currentFile = file;
        previewElement.innerHTML = '';
      });

      this.dropzone.on('success', (file, response) => {
        if (response && response.file_url) {
          const uploadedFile = {
            file_url: response.file_url,
            mime_type: file.type
          };
          this.currentFiles = [uploadedFile];
          this.updatePreview(uploadedFile);
        }
      });

      this.dropzone.on('error', (file, message) => {
        this.dropzone.removeAllFiles(true);
        previewElement.innerHTML = '';
        this.createDefaultPreview(previewElement);
        store.dispatch(addNotification({
          type: 'error',
          content: {
            heading: 'Upload Error',
            message: typeof message === 'string' ? message : 'Upload failed. Please try again.'
          }
        }));
      });

    } catch (error) {
      console.error('Failed to initialize Dropzone:', error);
    }
  }

  static styles = [
    super.styles,
    css`
      :host {
        --slds-c-input-color-background: var(--background-color);
        --slds-c-input-color-border: var(--border-color);
      }
    .fixed {
      max-width: 70rem;
      margin-left: auto;
      margin-right: auto;
    }
    .dropzone {
      border: 2px dashed #0087f7;
      border-radius: 5px;
      min-height: 150px;
      padding: 20px;
      text-align: center;
      cursor: pointer;
    }
    .dark .slds-box {
      border: 1px solid #514f4d;
    }
    .dark input {
      background-color: #2b2826;
    }
    .dropzone .dz-message {
      margin: 1em 0;
    }
    .no-linebreak {
      white-space: nowrap;
    }
    @media only screen and (max-width: 63.95rem) {
      .fixBox {
        margin-right: 0;
        margin-bottom: 1rem;
      }
    }
    .slds-form-element__label {
      color: var(--text-color);
    }
    button.dz-button {
      background-color: var(--background-color);
      border: var(--background-color);
    }
    button.slds-button {
      color: var(--text-color, white);
    }
    .dz-error-message>span {
      color: #ea001e;
    }
    button.slds-button.slds-button_icon.slds-button_icon-border {
      color: var(--text-color, #181818);
    }
    .slds-button--icon-border, .slds-button--icon-border-filled, .slds-button_icon-border, .slds-button_icon-border-filled {
      border: 1px solid var(--slds-c-input-color-border, #c9c9c9);
    }
      .preview-container {
        margin: 1rem 0;
      }
    `
  ];

  handleOptionSelected(event) {
    const selectedId = event.detail.value;
    localStorage.setItem('selectedCategoryId', JSON.stringify(selectedId));
    this.selectedCategory = selectedId;
  }

  updateCharCount(event) {
    const input = event.target;
    const maxLength = input.maxLength;
    const currentLength = input.value.length;
    const charCount = `${currentLength}/${maxLength}`;
    
    const counter = this.shadowRoot.querySelector('#char-count');
    if (counter) {
      counter.textContent = charCount;
    }
    
    this.title = input.value;
    localStorage.setItem('postTitleInput', input.value);
  }

  handleSubmit(event) {
    event.preventDefault();
    this.sendingRequest = true;
    store.dispatch(createPost({
      title: this.title,
      category: this.selectedCategory,
      images: this.currentFiles,
      tags: this.selectedTags,
    }));
  }

  async useLink(event) {
    event.preventDefault();
    const urlInput = this.shadowRoot.querySelector('#url-input');
    const url = urlInput.value.trim();

    if (!url) {
      urlInput.focus();
      return;
    }

    this.uploadingUrl = true;
    // Set cursor to loading
    urlInput.style.cursor = 'wait';

    try {
      const response = await fetch(window.backendUrl + '/api/upload', {
        method: 'POST',
        headers: {
          'Authorization': this.accessToken,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ url })
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const result = await response.json();
      
      if (result && result.file_url) {
        this.currentFiles = [result];
        // Force immediate preview update
        requestAnimationFrame(() => {
          this.updatePreview(result);
        });
        urlInput.value = '';
      }
    } catch (error) {
      console.error('Error uploading URL:', error);
      store.dispatch(addNotification({
        type: 'error',
        content: {
          heading: 'URL Upload Failed',
          message: error.message
        }
      }));
    } finally {
      this.uploadingUrl = false;
      // Set cursor to default
      urlInput.style.cursor = 'default';
    }
  }

  render() {
    return html`
      <div class="fixed">
        <div class="slds-grid slds-wrap slds-p-around_medium">
          <div class="slds-col slds-large-size_2-of-3 slds-medium-size_1-of-1">
            <h1 class="slds-text-heading_large">Create Post</h1>
            <div class="slds-box slds-m-top_medium slds-m-right_x-large slds-m-bottom_x-large fixBox">
              <form @submit="${this.handleSubmit}">
                <div class="slds-form-element slds-m-bottom_medium">
                  <div class="slds-form-element">
                    <slds-combobox 
                      label="Select Category" 
                      .selected="${this.selectedCategory}" 
                      @option-selected="${this.handleOptionSelected}" 
                      placeholder="Select a Category…" 
                      .options="${this.categories}"
                    ></slds-combobox>
                  </div>
                </div>
                <div class="slds-form-element slds-m-bottom_medium">
                  <div class="slds-form-element__control slds-input-has-fixed-addon">
                    <input type="text" id="post-title-input" value="${this.title}" placeholder="Title" class="slds-input slds-text-heading_medium" @input="${this.updateCharCount}" maxlength="280" />
                    <span class="slds-form-element__addon" id="char-count">0/280</span>
                  </div>
                </div>
                <div id="dropzone" class="dropzone">
                  <div id="previews"></div>
                  <div class="dz-default dz-message"></div>
                  <button type="button" class="slds-button slds-button_brand" @click="${this.chooseFile}">Choose File...</button>
                  <p class="slds-p-top_medium slds-p-bottom_medium">OR</p>
                  <div class="slds-form-element slds-text-align_left slds-p-right_large slds-p-left_large">
                    <div class="slds-form-element__control slds-input-has-fixed-addon">
                      <input type="text" id="url-input" placeholder="Paste image or video URL" class="slds-input" />
                      <span class="slds-form-element__addon">
                        <button type="button" class="slds-button slds-button_brand no-linebreak" @click="${this.useLink}" ?disabled="${this.uploadingUrl}">Use Link</button>
                      </span>
                    </div>
                    <label class="slds-form-element__label" for="url-input">Supports links with PNG, JPG, GIF, WEBP, WEBM or MP4 file.</label>
                  </div>
                </div>
                <slds-lookup
                  placeholder="Add at least 1 tag"
                  id="tag-lookup"
                  .selectedItems="${this.selectedTags}"
                  @selected="${this.handleTagSelected}"
                  isCombobox></slds-lookup>
                <button type="button" class="slds-button slds-button_icon slds-m-top_medium slds-button_icon-border" title="Settings">
                  <svg class="slds-button__icon" aria-hidden="true">
                    <use xlink:href="/assets/icons/utility-sprite/svg/symbols.svg#settings"></use>
                  </svg>
                  <span class="slds-assistive-text">Settings</span>
                </button>
                <button type="submit" class="slds-button slds-button_brand slds-m-top_medium slds-float_right" ?disabled="${!this.selectedCategory||this.sendingRequest}">Post</button>
            </form>
            </div>
          </div>
          <div class="slds-col slds-large-size_1-of-3 slds-medium-size_1-of-1">
            <div class="slds-box">
              <h2 class="slds-text-heading_medium">Roflcopter Rules</h2>
              <ol class="slds-list_ordered slds-text-title_bold" style="margin-left: 1rem;">
                <li class="slds-p-top_large">No blasphemy</li>
                <li class="slds-p-top_large">No pornography</li>
                <li class="slds-p-top_large">No violence or gory contents</li>
                <li class="slds-p-top_large">No hate speech and bullying</li>
                <li class="slds-p-top_large">No spamming and manipulation</li>
                <li class="slds-p-top_large">No deceptive content</li>
                <li class="slds-p-top_large">No illegal activities</li>
                <li class="slds-p-top_large">No impersonation</li>
                <li class="slds-p-top_large slds-p-bottom_x-small">No copyright infringement</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    `
  }
}